import { ReactNode } from 'react';

export interface DataListItem {
  label: ReactNode;
  value: ReactNode;
  /** Opcjonalny tooltip / helper text pod value. */
  hint?: ReactNode;
}

export interface DataListProps {
  items: DataListItem[];
  /** horizontal = label po lewej, value po prawej w jednym wierszu.
   *  vertical = label nad value. Default: horizontal. */
  orientation?: 'horizontal' | 'vertical';
  /** Gęstsze paddingi (dla modali / paneli). */
  dense?: boolean;
  /** Gdy true - pokazuje separatory między itemami. Default: true. */
  dividers?: boolean;
  className?: string;
}

/**
 * Lista par label/value dla details panels, invoice summary, preview kart.
 * Używaj zamiast ręcznie budowanych `<div class="flex justify-between">`
 * wzorców w kodzie.
 */
export function DataList({
  items,
  orientation = 'horizontal',
  dense = false,
  dividers = true,
  className,
}: DataListProps) {
  const isHorizontal = orientation === 'horizontal';
  const rowPad = dense ? 'py-1.5' : 'py-2.5';

  return (
    <dl className={className}>
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <div
            key={idx}
            className={[
              rowPad,
              isHorizontal ? 'flex items-baseline justify-between gap-4' : 'flex flex-col gap-1',
              dividers && !isLast ? 'border-b border-border-subtle' : '',
            ].filter(Boolean).join(' ')}
          >
            <dt className={[
              'text-sm font-medium text-text-muted',
              isHorizontal ? 'shrink-0' : '',
            ].filter(Boolean).join(' ')}>
              {item.label}
            </dt>
            <dd className={[
              'text-xs text-text-primary',
              isHorizontal ? 'text-right min-w-0' : '',
            ].filter(Boolean).join(' ')}>
              {item.value}
              {item.hint && (
                <div className="text-xxs text-text-muted mt-0.5 font-normal">{item.hint}</div>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
